import { useEffect, useState, type ReactNode } from 'react'
import {
  isValidCoords,
  readGeolocationPermission,
  requestCurrentPosition,
  type Coords,
  type GeolocationPermission,
} from '@/lib/geolocation'
import { UserLocationContext } from './UserLocationContext'

const STORAGE_KEY = 'matevolno.userLocation'

function loadStoredCoords(): Coords | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isValidCoords(parsed) ? parsed : null
  } catch {
    return null
  }
}

function storeCoords(coords: Coords): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(coords))
  } catch {
    // Ignore storage errors (private mode, quota, etc.).
  }
}

export function UserLocationProvider({ children }: { children: ReactNode }) {
  const [coords, setCoords] = useState<Coords | null>(() => loadStoredCoords())
  const [permission, setPermission] = useState<GeolocationPermission>('prompt')
  const [isRequesting, setIsRequesting] = useState(false)

  useEffect(() => {
    let active = true
    // readGeolocationPermission never rejects (it falls back to 'prompt').
    void readGeolocationPermission().then((state) => {
      if (!active) return
      setPermission(state)
      // Already granted: fetch silently so the user never sees a prompt.
      if (state === 'granted' && loadStoredCoords() === null) {
        requestCurrentPosition()
          .then((fresh) => {
            if (!active) return
            setCoords(fresh)
            storeCoords(fresh)
          })
          .catch(() => {})
      }
    })
    return () => {
      active = false
    }
  }, [])

  const requestLocation = async () => {
    setIsRequesting(true)
    try {
      const fresh = await requestCurrentPosition()
      setCoords(fresh)
      storeCoords(fresh)
      setPermission('granted')
    } catch {
      setPermission(await readGeolocationPermission())
    } finally {
      setIsRequesting(false)
    }
  }

  return (
    <UserLocationContext.Provider value={{ coords, permission, isRequesting, requestLocation }}>
      {children}
    </UserLocationContext.Provider>
  )
}
